import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import {
	BroadcastService,
	ORDER_EVENT_TYPE,
	SignalRState,
	ToasterType,
	SignalRService,
	GENERAL_EVENT_TYPE,
	ChangeEventItemOnChannelAction,
	SubContentEventType,
	SubContentService
} from '@cv-invision/core';
import { DialogService } from 'primeng/dynamicdialog';
import { OrderActivitDialogComponent } from '../../../inbox/dialogs/order-activity/order-activity-dialog.component';
// State
import { QualityState } from '../../state/quality.state';
import {
	SetStateSelectedOrder,
	SetOrderDocumentManually,
	SetOrderisRecalled
} from '../../state/actions/quality.actions';
// Models
import { ORDER_ORIGIN, ORDER_STATE } from '../../model/quality.constant';
import { OrderDetailModel, OrderLockedBy } from '../../state/model/order-detail.model';
// Service
import { OverviewSubHeaderService } from './service/overview-sub-header.service';
import { QualityOrderDetailState } from '../../state/quality-order-detail.state';

@Component({
	selector: 'cv-overview-sub-header',
	templateUrl: './overview-sub-header.component.html',
	styleUrls: ['./overview-sub-header.component.scss'],
	host: {
		class: 'd-block w-100 flex-shrink-0'
	}
})
export class OverviewSubHeaderComponent implements OnInit, OnDestroy {
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	lockedBy$: Observable<OrderLockedBy> = this.store.select(QualityOrderDetailState.getOrderLockedBy);

	orderInboxModel: OrderDetailModel;
	lockedBy: OrderLockedBy;
	currentUser: any;
	orderUsers: any[];
	isSidebarOpen = true;
	isManualOrder = false;
	isCmpAcuityOrder = false;

	private atComponentDestroy = new Subject();

	constructor(
		private broadcastService: BroadcastService,
		private overviewSubheaderService: OverviewSubHeaderService,
		private signalRService: SignalRService,
		private subContentService: SubContentService,
		private router: Router,
		private store: Store,
		private dialogService: DialogService
	) {
		this.orderUsers = [];
		this.currentUser = this.store.selectSnapshot((state) => state.user);
		this.reloadPage();
	}

	ngOnInit(): void {
		this.orderDetail$.pipe(takeUntil(this.atComponentDestroy)).subscribe((order) => {
			this.orderInboxModel = order;
			this.isManualOrder = this.orderInboxModel.externalTrackingProvider === ORDER_ORIGIN.MANUAL;
			this.isCmpAcuityOrder = this.orderInboxModel.externalTrackingProvider === ORDER_ORIGIN.CMP_ACUITY;
		});

		this.lockedBy$.pipe(takeUntil(this.atComponentDestroy)).subscribe((lockedBy) => {
			this.lockedBy = lockedBy;
			this.overviewSubheaderService.setOrderLockedBy(this.lockedBy, this.currentUser);
		});

		this.isSidebarOpen = this.subContentService.isSubContentPanelOpen;

		this.getCurrentUserList();

		this.channels$.pipe(takeUntil(this.atComponentDestroy)).subscribe((channels: any) => {
			if (channels && channels[`orders:${this.orderInboxModel.id}`]) {
				this.onOrderEvents(channels[`orders:${this.orderInboxModel.id}`].events);
			}
		});

		this.overviewSubheaderService.subheaderEvents
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result: any) => {
				switch (result.name) {
					case 'RETURN_HOME':
						this.returnToMainModule();
						break;
				}
			});

		this.broadcastService
			.onEvent(SubContentEventType.OPEN_CLOSE)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				if (event?.event && typeof event.event.open === 'boolean') {
					this.isSidebarOpen = event.event.open;
				}
			});
	}

	/**
	 * reload page and get new data for server //  good bye, SPA concept
	 */
	reloadPage(): void {
		window.addEventListener('beforeunload', () => {
			// Reload the order every time we refresh the page from browser
			const orderDetailModel = this.store.selectSnapshot(QualityState.getOrderDetail);
			this.store.dispatch(new SetOrderisRecalled(null));
			if (orderDetailModel && orderDetailModel.id) {
				this.store.dispatch(new SetOrderDocumentManually(null));
			}
		});
	}

	/**
	 * get the list of users on the order
	 */
	private getCurrentUserList(): void {
		this.signalRService
			.invokeHubPublic(GENERAL_EVENT_TYPE.MEMBERS, `orders:${this.orderInboxModel.id}`)
			.then((data) => {
				const users = data.filter((u) => u.id !== this.currentUser.id);
				if (users.length) {
					const us = users.map((u) => {
						return {
							...u,
							initials: u.fullName
								.split(' ')
								.map((x) => x.charAt(0))
								.join('')
								.substr(0, 2)
								.toUpperCase()
						};
					});
					this.orderUsers = us;
				} else {
					this.orderUsers = users;
				}
			})
			.catch(() => {
				this.orderUsers = [];
			});
	}

	/**
	 * On any current order related events analyze the action and apply changes to order model.
	 * @param events
	 */
	private onOrderEvents(channelEvents: Array<any>): void {
		const userSc = channelEvents.find(
			(item) =>
				item.event === ORDER_EVENT_TYPE.ORDER_SUBSCRIBED ||
				item.event === ORDER_EVENT_TYPE.ORDER_UNSUBSCRIBED ||
				item.event === GENERAL_EVENT_TYPE.USER_DISCONNECTED
		);
		if (userSc) {
			this.getCurrentUserList();
		}
		this.getEventNotificationMessages(channelEvents);
	}

	/**
	 * Detects Special Event Notifications
	 */
	getEventNotificationMessages(channelEvents: Array<any>): void {
		// If there is any special event, show it
		channelEvents.forEach((channelEvent: any) => {
			let message = '';
			if (channelEvent.status === 'new') {
				switch (channelEvent.event) {
					case ORDER_EVENT_TYPE.QRR_REPORT_GENERATED:
						this.overviewSubheaderService.subheaderEvents.next({ name: channelEvent.event });
						break;
					case ORDER_EVENT_TYPE.ORDER_REJECTED:
						if (channelEvent.data.user.fullName === '') {
							message = `Order <span class="order-id"${channelEvent.data.orderId}</span> was rejected by another user`;
						} else {
							message = `${channelEvent.data.user.fullName} has rejected the current Order ${channelEvent.data.orderId}.`;
						}
						this.store.dispatch(new SetStateSelectedOrder(ORDER_STATE.REJECTED)).subscribe(() => {
							this.orderInboxModel = this.store.selectSnapshot(QualityState.getOrderDetail);
						});
						break;
					case ORDER_EVENT_TYPE.ORDER_APPROVED:
						if (channelEvent.data.user.fullName === '') {
							message = `Order <span class="order-id"${channelEvent.data.orderId}</span> was approved by another user`;
						} else {
							message = `${channelEvent.data.user.fullName} has approved the current Order ${channelEvent.data.orderId}.`;
						}
						this.store.dispatch(new SetStateSelectedOrder(ORDER_STATE.APPROVED)).subscribe(() => {
							this.orderInboxModel = this.store.selectSnapshot(QualityState.getOrderDetail);
						});
						break;
					case ORDER_EVENT_TYPE.ORDER_RESUBMITTED: {
						const { isValid } = channelEvent.data;
						if (isValid !== undefined && isValid === false) {
							message = `${channelEvent.data.message}`;
							break;
						}
						if (channelEvent.data.user.fullName === '') {
							message = `Order <span class="order-id"${channelEvent.data.orderId}</span> was recalled by another user`;
						} else if (channelEvent.data.user.id !== this.currentUser.id) {
							message = `${channelEvent.data.user.fullName} has recalled the current Order ${channelEvent.data.orderId}.`;
						} else {
							message = `Order <span class="order-id"${channelEvent.data.orderId}</span> was successfully recalled`;
						}
						const { orderId } = channelEvent.data;
						this.overviewSubheaderService.recallaNewOrder(orderId);
						this.store.dispatch(new SetOrderisRecalled(null));
						break;
					}
				}

				this.overviewSubheaderService.subheaderEvents.next({
					name: channelEvent.event,
					data: channelEvent.data
				});

				if (message.length > 0) {
					// Change event to old
					this.store.dispatch(
						new ChangeEventItemOnChannelAction('orders:' + this.orderInboxModel.id, channelEvent, 'orderId')
					);
					// Show user message
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: { message: message }
					});
				}
			}
		});
	}

	/**
	 * Opens the order activity log dialog.
	 */
	openOrderActivity(): void {
		if (!this.orderInboxModel?.id) {
			return;
		}
		this.dialogService.open(OrderActivitDialogComponent, {
			width: '718px',
			height: '640px',
			header: 'Activity Log',
			data: {
				orderId: this.orderInboxModel.id
			},
			styleClass: 'order-activity-dialog'
		});
	}

	/**
	 * Toggles the Checklist (sub-content) panel.
	 */
	toggleSidebar(): void {
		this.isSidebarOpen = !this.isSidebarOpen;
		this.broadcastService.broadcast({
			name: SubContentEventType.OPEN_CLOSE,
			event: {
				open: this.isSidebarOpen,
				width: this.isSidebarOpen ? '0.8' : undefined
			}
		});
	}

	/**
	 * return to the main intelligence module
	 */
	returnToMainModule(): void {
		setTimeout(() => {
			this.router.navigate(['/intelligence/inbox']);
		}, 500);
	}

	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
